import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Truck, Navigation, MapPin, RefreshCw, Maximize2, Filter,
  Clock, Fuel, AlertTriangle, Phone, MessageSquare, Eye,
  Locate, Layers, Route, ChevronRight, X, Settings } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { useFleetAPI, useVehicles } from '../hooks/useFleetData';
import { getVehicleIllustration } from '../components/VehicleIllustrations';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

// Custom vehicle marker icons
const createVehicleIcon = (status, vehicleType) => {
  const colors = {
    available: '#22c55e',
    on_route: '#3b82f6',
    loading: '#f59e0b',
    unloading: '#f59e0b',
    maintenance: '#f97316',
    offline: '#6b7280',
    AVAILABLE: '#22c55e',
    ON_ROUTE: '#3b82f6',
    LOADING: '#f59e0b',
    UNLOADING: '#f59e0b',
    MAINTENANCE: '#f97316',
    OFFLINE: '#6b7280'
  };

  const color = colors[status] || '#6b7280';

  return L.divIcon({
    className: 'custom-vehicle-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 18px;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10 17h4V5H2v12h3"/>
          <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/>
          <circle cx="7.5" cy="17.5" r="2.5"/>
          <circle cx="17.5" cy="17.5" r="2.5"/>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

// Map center updater component
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
};

const LiveTrackingPage = () => {const { t } = useTranslation("fleet");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleList, setShowVehicleList] = useState(true);
  const [mapCenter, setMapCenter] = useState([-23.5505, -46.6333]); // São Paulo default
  const [mapZoom, setMapZoom] = useState(12);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [vehiclePositions, setVehiclePositions] = useState([]);
  const mapRef = useRef(null);

  const { vehicles, loading, refresh } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  // Simulate vehicle positions (in real app, this would come from GPS tracking API)
  useEffect(() => {
    if (vehicles.length > 0) {
      const positions = vehicles.map((v, idx) => ({
        ...v,
        // Simulate positions around São Paulo
        lat: -23.5505 + (Math.random() - 0.5) * 0.2,
        lng: -46.6333 + (Math.random() - 0.5) * 0.3,
        speed: Math.floor(Math.random() * 80),
        heading: Math.floor(Math.random() * 360),
        lastUpdate: new Date().toISOString()
      }));
      setVehiclePositions(positions);
    }
  }, [vehicles]);

  // Auto-refresh positions
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setVehiclePositions((prev) => prev.map((v) => ({
        ...v,
        lat: v.lat + (Math.random() - 0.5) * 0.001,
        lng: v.lng + (Math.random() - 0.5) * 0.001,
        speed: Math.max(0, v.speed + Math.floor((Math.random() - 0.5) * 10)),
        lastUpdate: new Date().toISOString()
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Filter vehicles
  const filteredVehicles = vehiclePositions.filter((v) => {
    const matchesSearch =
    v.vehicle_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.make?.toLowerCase().includes(searchTerm.toLowerCase());

    const normalizedStatus = (v.status || '').toLowerCase();
    const matchesFilter = filterStatus === 'all' || normalizedStatus === filterStatus;

    return matchesSearch && matchesFilter;
  });

  // Focus on vehicle
  const focusOnVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setMapCenter([vehicle.lat, vehicle.lng]);
    setMapZoom(15);
  };

  // Status counts
  const statusCounts = vehiclePositions.reduce((acc, v) => {
    const status = (v.status || 'OFFLINE').toLowerCase();
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusConfig = {
    available: { color: 'bg-emerald-500', label: 'Available' },
    on_route: { color: 'bg-blue-500', label: 'On Route' },
    loading: { color: 'bg-amber-500', label: 'Loading' },
    maintenance: { color: 'bg-orange-500', label: 'Maintenance' },
    offline: { color: 'bg-gray-400', label: 'Offline' }
  };

  return (
    <div className={`flex h-[calc(100vh-64px)] bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Left Panel - Vehicle List */}
      {showVehicleList &&
      <div className="w-80 bg-white border-r flex flex-col relative z-10">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-500" />{t("liveTracking", "Live Tracking")}

            </h2>
              <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVehicleList(false)}>
              
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
              placeholder="Search vehicles..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            
            </div>
          </div>

          {/* Status Filter */}
          <div className="p-3 border-b flex flex-wrap gap-2">
            <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
            filterStatus === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
            }>{t("all", "All (")}

            {vehiclePositions.length})
            </button>
            {Object.entries(statusConfig).map(([key, config]) =>
          <button
            key={key}
            onClick={() => setFilterStatus(key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
            filterStatus === key ? `${config.color} text-white` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
            }>
            
                <span className={`w-2 h-2 rounded-full ${filterStatus === key ? 'bg-white' : config.color}`}></span>
                {statusCounts[key] || 0}
              </button>
          )}
          </div>

          {/* Vehicle List */}
          <div className="flex-1 overflow-auto">
            {loading ?
          <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div> :
          filteredVehicles.length === 0 ?
          <div className="text-center py-8 text-gray-500">
                <Truck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t("noVehiclesFound", "No vehicles found")}</p>
              </div> :

          filteredVehicles.map((vehicle) => {
            const status = statusConfig[(vehicle.status || '').toLowerCase()] || statusConfig.offline;
            const isSelected = selectedVehicle?.id === vehicle.id;

            return (
              <div
                key={vehicle.id}
                onClick={() => focusOnVehicle(vehicle)}
                className={`p-3 border-b cursor-pointer transition-all hover:bg-gray-50 ${
                isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`
                }>
                
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${status.color} animate-pulse`}></div>
                        <span className="font-semibold text-gray-900">{vehicle.vehicle_code}</span>
                      </div>
                      <Badge className={`${status.color} text-white text-xs`}>
                        {status.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">
                      {vehicle.make} {vehicle.model} • {vehicle.license_plate}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Navigation className="w-3 h-3" />
                        {vehicle.speed}{t("kmh", "km/h")}
                  </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(vehicle.lastUpdate).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>);

          })
          }
          </div>

          {/* Auto Refresh Toggle */}
          <div className="p-3 border-t bg-gray-50">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-gray-600">{t("autorefresh5s", "Auto-refresh (5s)")}</span>
              <div
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`w-10 h-6 rounded-full transition-colors ${autoRefresh ? 'bg-green-500' : 'bg-gray-300'}`}>
              
                <div className={`w-4 h-4 bg-white rounded-full m-1 transition-transform ${autoRefresh ? 'translate-x-4' : ''}`}></div>
              </div>
            </label>
          </div>
        </div>
      }

      {/* Map Area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Map Controls */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          {!showVehicleList &&
          <Button
            size="sm"
            className="bg-white text-gray-700 shadow-lg hover:bg-gray-50"
            onClick={() => setShowVehicleList(true)}>
            
              <Truck className="w-4 h-4 mr-2" />{t("vehicles", "Vehicles")}

          </Button>
          }
          <Button
            size="sm"
            className="bg-white text-gray-700 shadow-lg hover:bg-gray-50"
            onClick={refresh}>
            
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            className="bg-white text-gray-700 shadow-lg hover:bg-gray-50"
            onClick={() => setIsFullscreen(!isFullscreen)}>
            
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            className="bg-white text-gray-700 shadow-lg hover:bg-gray-50"
            onClick={() => {
              setMapCenter([-23.5505, -46.6333]);
              setMapZoom(12);
            }}>
            
            <Locate className="w-4 h-4" />
          </Button>
        </div>

        {/* Status Legend */}
        <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-lg p-3">
          <p className="text-xs font-medium text-gray-500 mb-2">{t("vehicleStatus", "Vehicle Status")}</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusConfig).map(([key, config]) =>
            <div key={key} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
                <span className="text-xs text-gray-600">{config.label}</span>
              </div>
            )}
          </div>
        </div>

        {/* Leaflet Map */}
        <MapContainer
          ref={mapRef}
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}>
          
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          
          
          <MapCenterUpdater center={mapCenter} zoom={mapZoom} />
          
          {/* Vehicle Markers */}
          {filteredVehicles.map((vehicle) =>
          <Marker
            key={vehicle.id}
            position={[vehicle.lat, vehicle.lng]}
            icon={createVehicleIcon(vehicle.status, vehicle.vehicle_type)}
            eventHandlers={{
              click: () => setSelectedVehicle(vehicle)
            }}>
            
              <Popup>
                <div className="min-w-[200px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">{vehicle.vehicle_code}</span>
                    <Badge className={`${statusConfig[(vehicle.status || '').toLowerCase()]?.color || 'bg-gray-500'} text-white text-xs`}>
                      {statusConfig[(vehicle.status || '').toLowerCase()]?.label || 'Unknown'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {vehicle.make} {vehicle.model}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">{t("plate", "Plate:")}</span>
                      <span className="ml-1 font-medium">{vehicle.license_plate}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t("speed", "Speed:")}</span>
                      <span className="ml-1 font-medium">{vehicle.speed}{t("kmh", "km/h")}</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1 text-xs">
                      <Phone className="w-3 h-3 mr-1" />{t("call", "Call")}

                  </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs">
                      <Route className="w-3 h-3 mr-1" />{t("route", "Route")}

                  </Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Selected Vehicle Detail Panel */}
      {selectedVehicle &&
      <div className="w-80 bg-white border-l overflow-auto">
          <div className="p-4 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">{selectedVehicle.vehicle_code}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedVehicle(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Badge className={`${statusConfig[(selectedVehicle.status || '').toLowerCase()]?.color || 'bg-gray-500'} text-white mt-2`}>
              {statusConfig[(selectedVehicle.status || '').toLowerCase()]?.label || 'Unknown'}
            </Badge>
          </div>

          <div className="p-4 space-y-4">
            {/* Vehicle Info */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">{t("vehicleInfo", "Vehicle Info")}</h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t("makemodel", "Make/Model")}</span>
                  <span className="text-sm font-medium">{selectedVehicle.make} {selectedVehicle.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t("plate", "Plate")}</span>
                  <span className="text-sm font-medium">{selectedVehicle.license_plate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t("type", "Type")}</span>
                  <span className="text-sm font-medium">{selectedVehicle.vehicle_type}</span>
                </div>
              </div>
            </div>

            {/* Live Data */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">{t("liveData", "Live Data")}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <Navigation className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-blue-700">{selectedVehicle.speed}</p>
                  <p className="text-xs text-blue-500">{t("kmh", "km/h")}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <MapPin className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">{selectedVehicle.heading}°</p>
                  <p className="text-xs text-green-500">{t("heading", "Heading")}</p>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">{t("location", "Location")}</h4>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{t("coordinates", "Coordinates")}</p>
                <p className="text-sm font-mono">
                  {selectedVehicle.lat?.toFixed(6)}, {selectedVehicle.lng?.toFixed(6)}
                </p>
                <p className="text-xs text-gray-400 mt-2">{t("lastUpdate", "Last update:")}
                {new Date(selectedVehicle.lastUpdate).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">{t("quickActions", "Quick Actions")}</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" className="text-xs">
                  <Phone className="w-3 h-3 mr-1" />{t("callDriver", "Call Driver")}

              </Button>
                <Button variant="outline" size="sm" className="text-xs">
                  <MessageSquare className="w-3 h-3 mr-1" />{t("message", "Message")}

              </Button>
                <Button variant="outline" size="sm" className="text-xs">
                  <Route className="w-3 h-3 mr-1" />{t("viewRoute", "View Route")}

              </Button>
                <Button variant="outline" size="sm" className="text-xs">
                  <Eye className="w-3 h-3 mr-1" />{t("history", "History")}

              </Button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default LiveTrackingPage;